import React, { useEffect, useRef } from 'react'
import { CheckCircle2, ImagePlus, Loader2, X } from 'lucide-react'

import { toSydneyDatetimeLocal } from '../../../utils/sydneyDateTime'
function toLocalDatetimeInput(d) {
  const pad = (n) => String(n).padStart(2, '0')
  return toSydneyDatetimeLocal(d)
}

/**
 * Record AUSTRAC lodgement after external filing via AUSTRAC Online.
 * Filing notes + confirmation image go to the SMR registry; watchlist is member-only add.
 */
export default function RecordAustracSubmissionModal({
  open,
  onClose,
  onSubmit,
  submitting,
  austracRef,
  setAustracRef,
  filingMethod,
  setFilingMethod,
  filedAtLocal,
  setFiledAtLocal,
  filingNotes,
  setFilingNotes,
  addToWatchlist,
  setAddToWatchlist,
  onWatchlist,
  watchlistLoading,
  memberId,
  confirmationImage,
  setConfirmationImage,
}) {
  const fileInputRef = useRef(null)
  const pasteRef = useRef(null)

  useEffect(() => {
    if (!open) return
    const d = new Date()
    d.setSeconds(0, 0)
    setFiledAtLocal(toLocalDatetimeInput(d))
    setFilingMethod('manual')
    setAustracRef('')
    setFilingNotes('')
    setConfirmationImage(null)
  }, [open, setAustracRef, setFiledAtLocal, setFilingMethod, setFilingNotes, setConfirmationImage])

  const setImageFile = (file) => {
    if (!file || !file.type.startsWith('image/')) return
    const preview = URL.createObjectURL(file)
    setConfirmationImage({ file, preview, name: file.name || 'austrac-confirmation.png' })
  }

  const handlePaste = (e) => {
    const items = e.clipboardData?.items
    if (!items) return
    for (const item of items) {
      if (item.type.startsWith('image/')) {
        e.preventDefault()
        const file = item.getAsFile()
        if (file) setImageFile(file)
        break
      }
    }
  }

  if (!open) return null

  const hasMember = Boolean(memberId && String(memberId).trim() && memberId !== '—')
  const filingOk = austracRef.trim() && filedAtLocal && filingMethod

  return (
    <div className="fixed inset-0 z-[120] flex items-center justify-center p-4">
      <div className="absolute inset-0 bg-black/45" onClick={onClose} role="presentation" />
      <div
        className="relative bg-white rounded-xl shadow-xl w-full max-w-lg border border-slate-200 max-h-[92vh] overflow-y-auto"
        role="dialog"
        aria-labelledby="record-austrac-title"
      >
        <div className="flex items-center justify-between p-4 border-b border-slate-200">
          <div>
            <h3 id="record-austrac-title" className="text-lg font-bold text-slate-900">
              Record AUSTRAC submission
            </h3>
            <p className="text-xs text-slate-500 mt-0.5">
              File via AUSTRAC Online first, then record lodgement details here.
            </p>
          </div>
          <button type="button" onClick={onClose} className="p-1.5 rounded-lg hover:bg-slate-100" aria-label="Close">
            <X className="w-5 h-5 text-slate-500" />
          </button>
        </div>

        <div className="p-4 space-y-3">
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label htmlFor="smr-file-method" className="block text-xs font-semibold text-slate-500 uppercase tracking-wide mb-1">
                Filing method <span className="text-red-500">*</span>
              </label>
              <select
                id="smr-file-method"
                value={filingMethod}
                onChange={(e) => setFilingMethod(e.target.value)}
                className="w-full px-3 py-2 rounded-lg border border-slate-200 text-sm bg-white outline-none focus:border-slate-400"
              >
                <option value="manual">Manual (AUSTRAC Online)</option>
                <option value="portal">Portal (integrated)</option>
                <option value="system">System</option>
              </select>
            </div>
            <div>
              <label htmlFor="smr-file-at" className="block text-xs font-semibold text-slate-500 uppercase tracking-wide mb-1">
                Filed at <span className="text-red-500">*</span>
              </label>
              <input
                id="smr-file-at"
                type="datetime-local"
                value={filedAtLocal}
                onChange={(e) => setFiledAtLocal(e.target.value)}
                className="w-full px-3 py-2 rounded-lg border border-slate-200 text-sm outline-none focus:border-slate-400"
              />
            </div>
          </div>

          <div>
            <label htmlFor="smr-file-ref" className="block text-xs font-semibold text-slate-500 uppercase tracking-wide mb-1">
              AUSTRAC reference <span className="text-red-500">*</span>
            </label>
            <input
              id="smr-file-ref"
              type="text"
              maxLength={64}
              placeholder="From AUSTRAC Online confirmation"
              value={austracRef}
              onChange={(e) => setAustracRef(e.target.value.slice(0, 64))}
              className="w-full px-3 py-2 rounded-lg border border-slate-200 text-sm font-mono outline-none focus:border-slate-400"
            />
          </div>

          <div>
            <label htmlFor="smr-file-notes" className="block text-xs font-semibold text-slate-500 uppercase tracking-wide mb-1">
              Filing remark (optional)
            </label>
            <textarea
              id="smr-file-notes"
              rows={2}
              maxLength={4000}
              placeholder="Lodgement notes — stored on the SMR registry record"
              value={filingNotes}
              onChange={(e) => setFilingNotes(e.target.value.slice(0, 4000))}
              className="w-full px-3 py-2 rounded-lg border border-slate-200 text-sm text-slate-800 resize-y outline-none focus:border-slate-400"
            />
          </div>

          <div
            ref={pasteRef}
            tabIndex={0}
            onPaste={handlePaste}
            className="rounded-lg border border-dashed border-slate-300 bg-slate-50 p-3 focus:outline-none focus:border-slate-400"
          >
            <div className="text-xs font-semibold text-slate-500 uppercase tracking-wide mb-2">
              AUSTRAC confirmation (optional)
            </div>
            <p className="text-xs text-slate-500 mb-2">
              Upload or paste a screenshot of the AUSTRAC Online confirmation — saved to the SMR registry.
            </p>
            {confirmationImage?.preview ? (
              <div className="flex items-start gap-3">
                <img
                  src={confirmationImage.preview}
                  alt="AUSTRAC confirmation preview"
                  className="max-h-28 rounded border border-slate-200 object-contain bg-white"
                />
                <button
                  type="button"
                  onClick={() => setConfirmationImage(null)}
                  className="text-xs text-slate-600 underline hover:text-slate-900"
                >
                  Remove
                </button>
              </div>
            ) : (
              <div className="flex flex-wrap gap-2">
                <button
                  type="button"
                  onClick={() => fileInputRef.current?.click()}
                  className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg border border-slate-300 text-xs font-semibold text-slate-700 hover:bg-white"
                >
                  <ImagePlus className="w-3.5 h-3.5" />
                  Upload image
                </button>
                <span className="text-xs text-slate-400 self-center">or click here and paste (Ctrl+V)</span>
              </div>
            )}
            <input
              ref={fileInputRef}
              type="file"
              accept="image/*"
              className="hidden"
              onChange={(e) => {
                const f = e.target.files?.[0]
                if (f) setImageFile(f)
                e.target.value = ''
              }}
            />
          </div>

          {hasMember && (
            <div className="rounded-lg border border-slate-200 bg-slate-50 px-3 py-2.5">
              {watchlistLoading ? (
                <div className="flex items-center gap-2 text-xs text-slate-500">
                  <Loader2 className="w-3.5 h-3.5 animate-spin" />
                  Checking watchlist…
                </div>
              ) : onWatchlist ? (
                <div className="text-xs text-slate-600">
                  <span className="font-semibold text-slate-800">Already on watchlist</span>
                  {' — '}
                  patron will not be added again.
                </div>
              ) : (
                <label className="flex items-start gap-2.5 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={addToWatchlist}
                    onChange={(e) => setAddToWatchlist(e.target.checked)}
                    className="mt-0.5 rounded border-slate-300"
                  />
                  <span className="text-xs text-slate-700 leading-relaxed">
                    <span className="font-semibold text-slate-900">Add to watchlist</span>
                    {' — '}
                    add {memberId} to active monitoring after filing (no watchlist notes).
                  </span>
                </label>
              )}
            </div>
          )}

          {!hasMember && (
            <div className="text-xs text-amber-800 bg-amber-50 border border-amber-200 rounded-lg px-3 py-2">
              No linked member on this case — watchlist add is not available.
            </div>
          )}
        </div>

        <div className="p-4 border-t border-slate-200 bg-slate-50 flex justify-end gap-2 rounded-b-xl">
          <button
            type="button"
            onClick={onClose}
            disabled={submitting}
            className="px-4 py-2 rounded-lg border border-slate-300 text-sm text-slate-600 hover:bg-white disabled:opacity-50"
          >
            Cancel
          </button>
          <button
            type="button"
            disabled={!filingOk || submitting}
            onClick={onSubmit}
            className="inline-flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-semibold text-white disabled:opacity-50"
            style={{ background: '#dc2626' }}
          >
            {submitting ? (
              <Loader2 className="w-4 h-4 animate-spin" />
            ) : (
              <CheckCircle2 className="w-4 h-4" />
            )}
            {submitting ? 'Recording…' : 'Confirm filed'}
          </button>
        </div>
      </div>
    </div>
  )
}
